import { cloneDraftSnapshot, normalizeDraftSnapshot, type ComposerDraftSnapshot } from './snapshot';
import type { PerformanceTake } from './performance';

type Score = Pick<
  ComposerDraftSnapshot,
  | 'capturedEvents'
  | 'capturedPedalEvents'
  | 'timeSignatureId'
  | 'keySignatureId'
  | 'draftMeasureCount'
  | 'playbackTempo'
>;
type Session = Omit<ComposerDraftSnapshot, keyof Score | 'draftTitle' | 'performanceTakes'>;

export const COMPOSITION_SCHEMA_VERSION = 2;

export type CompositionPublicationStatus = 'pending' | 'processing' | 'processed' | 'error';

/**
 * Which composition revision was published to the repertoire, and under which
 * operation. Retrying a lost upload replays the same operation instead of creating
 * a second repertoire item.
 */
export type CompositionPublication = {
  operationId: string;
  publishedRevision: number;
  status: CompositionPublicationStatus;
  updatedAt: string;
  repertoireId?: string;
  message?: string;
};

/**
 * Version 2 adds retained-audio references on takes and repertoire publication
 * tracking. It keeps version 1's lossless adapter to the divisions-based notation
 * builder; audio bytes stay in local asset storage and never enter the document.
 */
export type CompositionDocument = {
  schemaVersion: 2;
  id: string;
  ownerId: string;
  title: string;
  revision: number;
  createdAt: string;
  updatedAt: string;
  score: Score;
  takes: PerformanceTake[];
  session: Session;
  conflictOf?: string;
  publication?: CompositionPublication;
  cloud?: { revision: number; syncedLocalRevision: number };
};

const isPublication = (value: unknown): value is CompositionPublication => {
  const publication = value as CompositionPublication | null;
  return Boolean(
    publication &&
    typeof publication.operationId === 'string' &&
    publication.operationId &&
    Number.isInteger(publication.publishedRevision) &&
    publication.publishedRevision >= 0 &&
    ['pending', 'processing', 'processed', 'error'].includes(publication.status) &&
    typeof publication.updatedAt === 'string' &&
    Number.isFinite(Date.parse(publication.updatedAt)) &&
    (publication.repertoireId === undefined || typeof publication.repertoireId === 'string') &&
    (publication.message === undefined || typeof publication.message === 'string'),
  );
};

export function documentFromSnapshot(
  snapshot: ComposerDraftSnapshot,
  ownerId: string,
  previous?: CompositionDocument,
): CompositionDocument {
  const {
    capturedEvents,
    capturedPedalEvents,
    timeSignatureId,
    keySignatureId,
    draftMeasureCount,
    playbackTempo,
    draftTitle,
    performanceTakes,
    ...session
  } = cloneDraftSnapshot(snapshot);
  const now = new Date().toISOString();
  return {
    schemaVersion: COMPOSITION_SCHEMA_VERSION,
    id: previous?.id ?? crypto.randomUUID(),
    ownerId,
    title: draftTitle,
    revision: previous?.revision ?? 0,
    createdAt: previous?.createdAt ?? now,
    updatedAt: now,
    score: {
      capturedEvents,
      capturedPedalEvents,
      timeSignatureId,
      keySignatureId,
      draftMeasureCount,
      playbackTempo,
    },
    takes: performanceTakes ?? [],
    session,
    ...(previous?.conflictOf ? { conflictOf: previous.conflictOf } : {}),
    ...(previous?.publication ? { publication: { ...previous.publication } } : {}),
    ...(previous?.cloud ? { cloud: { ...previous.cloud } } : {}),
  };
}

export function snapshotFromDocument(document: CompositionDocument): ComposerDraftSnapshot {
  const result = normalizeDraftSnapshot({
    ...document.session,
    ...document.score,
    draftTitle: document.title,
    performanceTakes: document.takes,
  });
  if (!result)
    throw new Error('This composition contains invalid score data. The stored copy was kept.');
  result.performanceTakes = result.performanceTakes?.map((take) =>
    take.status === 'recording' ? { ...take, status: 'interrupted' } : take,
  );
  return result;
}

/**
 * One step per stored version. A document written by an older build is upgraded on
 * read; a document from a newer build is rejected rather than silently truncated.
 */
const upgradeToCurrentSchema = (value: unknown): unknown => {
  const document = value as { schemaVersion?: unknown } | null;
  if (!document || typeof document !== 'object') return document;
  // Version 1 had no retained audio and no publication record. Both are optional in
  // version 2, so the upgrade adds no fields and invents no publication history.
  if (document.schemaVersion === 1)
    return { ...document, schemaVersion: COMPOSITION_SCHEMA_VERSION };
  return document;
};

export function parseComposition(value: unknown): CompositionDocument {
  const document = upgradeToCurrentSchema(value) as CompositionDocument | null;
  if (
    !document ||
    document.schemaVersion !== COMPOSITION_SCHEMA_VERSION ||
    (document.publication !== undefined && !isPublication(document.publication)) ||
    typeof document.id !== 'string' ||
    !document.id ||
    typeof document.ownerId !== 'string' ||
    !document.ownerId ||
    typeof document.title !== 'string' ||
    !Number.isInteger(document.revision) ||
    document.revision < 0 ||
    !Number.isFinite(Date.parse(document.createdAt)) ||
    !Number.isFinite(Date.parse(document.updatedAt)) ||
    !document.score ||
    !document.session ||
    !Array.isArray(document.takes)
  ) {
    throw new Error('This composition file is invalid or uses a newer format.');
  }
  snapshotFromDocument(document);
  return document;
}

/** A stale writer gets an independent recoverable copy, never a silent overwrite. */
export function prepareRevision(
  document: CompositionDocument,
  stored?: CompositionDocument,
): CompositionDocument {
  const conflict = stored && stored.revision !== document.revision;
  const takes = new Map(document.takes.map((take) => [take.id, take]));
  // Finalized observations are an archive. Editing/undo may add takes, but can
  // never rewrite or remove a take that was already durably acknowledged.
  if (stored && !conflict) {
    for (const take of stored.takes) {
      if (take.status !== 'recording') takes.set(take.id, take);
    }
  }
  return {
    ...document,
    takes: Array.from(takes.values()),
    id: conflict ? crypto.randomUUID() : document.id,
    title: conflict ? `${document.title} (conflict copy)` : document.title,
    revision: conflict ? 1 : document.revision + 1,
    updatedAt: new Date().toISOString(),
    ...(conflict ? { conflictOf: document.id } : {}),
    // A conflict copy is a new composition that has never been published. Inheriting
    // the original's publication would point two documents at one repertoire item.
    publication: conflict ? undefined : document.publication,
    cloud: conflict ? undefined : (stored?.cloud ?? document.cloud),
  };
}
